import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, ArrowRight, Spinner, ShieldCheck, Tag, Copy, CheckCircle, ChartLineUp } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";

const initial = { name: "", email: "", phone: "" };

const HOW_IT_WORKS = [
  { icon: Gift, title: "Get your code", desc: "Fill in your name and email — we generate a personal referral code just for you (takes 5 seconds)." },
  { icon: ArrowRight, title: "Share with anyone", desc: "Text it, email it, or share it on Facebook. Anyone who books a Joseph Industries service using your code saves $25." },
  { icon: CheckCircle, title: "You both get $25 off", desc: "When we complete their job, you get a $25 credit toward your next service. Stack unlimited codes." },
];

export default function ReferPage() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [refCode, setRefCode] = useState(null);
  const [errors, setErrors] = useState({});

  // Lookup mode — for existing referrers to check their stats
  const [lookupCode, setLookupCode] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onGenerate = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/referrals/generate", {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
      });
      setRefCode(data);
      toast.success(`Your referral code is ${data.code}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create referral code");
    } finally {
      setSubmitting(false);
    }
  };

  const onLookup = async (ev) => {
    ev.preventDefault();
    const code = lookupCode.trim().toUpperCase();
    if (!code) return;
    setLookupLoading(true);
    try {
      const { data } = await api.get(`/referrals/${code}/summary`);
      setSummary(data);
    } catch (err) {
      toast.error("Code not found. Check spelling.");
      setSummary(null);
    } finally {
      setLookupLoading(false);
    }
  };

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("Copied to clipboard!");
    } catch {
      toast.error("Copy failed — please select the code manually.");
    }
  };

  const shareText = refCode
    ? `Use my Joseph Industries referral code ${refCode.code} at josephindustries.site/contact and get $25 off your next appliance repair, courier, cleanup, or moving service.`
    : "";

  return (
    <div data-testid="refer-page">
      <section className="ji-container pt-16 md:pt-24 pb-10">
        <div className="ji-overline mb-5 inline-flex items-center gap-2">
          <Gift size={12} weight="fill" /> Refer &amp; Earn
        </div>
        <h1 style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }} className="text-5xl md:text-6xl font-bold tracking-tight max-w-3xl">
          Refer a friend —<br /><span className="text-[var(--ji-accent)] italic">both of you get $25 off.</span>
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-[var(--ji-muted)] leading-relaxed">
          Know somebody who needs an appliance fixed, a moving hand, or a home cleaned up? Send them your code.
          When they book, they save <strong className="text-[var(--ji-text)]">$25</strong>. Once we finish the job, you get a{" "}
          <strong className="text-[var(--ji-text)]">$25 credit</strong> toward your next Joseph Industries service. No limit — stack as many as you like.
        </p>
      </section>

      {/* How it works */}
      <section className="ji-container pb-8" data-testid="refer-how-it-works">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {HOW_IT_WORKS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.title} className="ji-card p-6" style={{ borderRadius: 4 }} data-testid={`refer-step-${i}`}>
                <div className="flex items-center gap-3">
                  <span className="inline-flex w-11 h-11 items-center justify-center bg-[var(--ji-surface-alt)] text-[var(--ji-primary)]" style={{ borderRadius: 4 }}>
                    <Icon size={22} weight="duotone" />
                  </span>
                  <span style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold text-[var(--ji-accent)]">0{i + 1}</span>
                </div>
                <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="mt-5 text-lg font-bold tracking-tight">{s.title}</h3>
                <p className="mt-2 text-sm text-[var(--ji-muted)] leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Main content: generate + lookup */}
      <section className="ji-container pb-24 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Generate new code */}
        <div className="lg:col-span-7">
          {!refCode ? (
            <form onSubmit={onGenerate} data-testid="refer-generate-form" className="ji-card p-7 md:p-10 space-y-5" style={{ borderRadius: 4 }}>
              <div className="ji-overline inline-flex items-center gap-2"><Tag size={12} weight="fill" /> Get your personal code</div>
              <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight">Create your referral code.</h2>
              <p className="text-sm text-[var(--ji-muted)] leading-relaxed">
                We only use this to send you a notification when someone books using your code and to credit your account.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-2">
                <div className="md:col-span-2">
                  <Label htmlFor="r-name" className="text-sm font-medium">Full name *</Label>
                  <Input id="r-name" data-testid="refer-name" value={form.name} onChange={(e) => update("name", e.target.value)} className="h-12 mt-2" placeholder="Jane Doe" />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="r-email" className="text-sm font-medium">Email *</Label>
                  <Input id="r-email" type="email" data-testid="refer-email" value={form.email} onChange={(e) => update("email", e.target.value)} className="h-12 mt-2" placeholder="you@email.com" />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>
                <div>
                  <Label htmlFor="r-phone" className="text-sm font-medium">Phone <span className="text-[var(--ji-muted)] font-normal">— optional</span></Label>
                  <Input id="r-phone" data-testid="refer-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="h-12 mt-2" placeholder="(504) 555-0100" />
                </div>
              </div>
              <div className="pt-2">
                <button type="submit" data-testid="refer-submit" disabled={submitting}
                  className="ji-btn-accent w-full inline-flex items-center justify-center gap-2 px-6 h-14 text-base font-semibold disabled:opacity-60"
                  style={{ borderRadius: 4 }}
                >
                  {submitting ? <><Spinner size={18} className="animate-spin" /> Generating…</> :
                    <>Get my referral code <ArrowRight size={18} weight="bold" /></>}
                </button>
              </div>
            </form>
          ) : (
            <div className="ji-card p-7 md:p-10 space-y-6" style={{ borderRadius: 4 }} data-testid="refer-code-card">
              <div className="ji-overline inline-flex items-center gap-2 text-[var(--ji-accent)]">
                <CheckCircle size={14} weight="fill" /> Your code is ready
              </div>
              <div className="p-8 bg-[var(--ji-surface-alt)] border-2 border-dashed border-[var(--ji-accent)]" style={{ borderRadius: 4 }}>
                <div className="text-xs uppercase tracking-[0.22em] text-[var(--ji-muted)]">Your code</div>
                <div className="mt-3 flex items-baseline justify-between gap-4">
                  <div style={{ fontFamily: "Cabinet Grotesk", letterSpacing: "0.1em" }} className="text-4xl md:text-5xl font-bold text-[var(--ji-primary)] break-all" data-testid="refer-code-value">
                    {refCode.code}
                  </div>
                  <button
                    type="button"
                    data-testid="refer-copy-code"
                    onClick={() => copyCode(refCode.code)}
                    className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold flex-shrink-0"
                    style={{ borderRadius: 4 }}
                  >
                    <Copy size={14} weight="bold" /> Copy
                  </button>
                </div>
              </div>
              <div>
                <div className="text-sm font-medium mb-2">Share it — copy this message:</div>
                <div className="p-4 bg-[var(--ji-surface-alt)] text-sm leading-relaxed border border-[var(--ji-border)]" style={{ borderRadius: 4 }} data-testid="refer-share-text">
                  {shareText}
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" data-testid="refer-copy-share" onClick={() => copyCode(shareText)} className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold" style={{ borderRadius: 4 }}>
                    <Copy size={14} weight="bold" /> Copy message
                  </button>
                  <a
                    data-testid="refer-share-sms"
                    href={`sms:?&body=${encodeURIComponent(shareText)}`}
                    className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold"
                    style={{ borderRadius: 4 }}
                  >
                    Text it
                  </a>
                  <a
                    data-testid="refer-share-email"
                    href={`mailto:?subject=${encodeURIComponent("$25 off Joseph Industries")}&body=${encodeURIComponent(shareText)}`}
                    className="ji-btn-outline inline-flex items-center gap-2 px-4 h-11 text-sm font-semibold"
                    style={{ borderRadius: 4 }}
                  >
                    Email it
                  </a>
                </div>
              </div>
              <div className="pt-2 border-t border-[var(--ji-border)]">
                <button
                  type="button"
                  data-testid="refer-reset"
                  onClick={() => { setRefCode(null); setForm(initial); }}
                  className="text-sm text-[var(--ji-muted)] hover:text-[var(--ji-primary)] underline"
                >
                  Create another code for a different email
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Sidebar: lookup existing code + trust card */}
        <aside className="lg:col-span-5 space-y-5">
          <div className="ji-card p-7" style={{ borderRadius: 4 }} data-testid="refer-lookup-card">
            <div className="ji-overline mb-3 inline-flex items-center gap-2"><ChartLineUp size={12} weight="fill" /> Check your rewards</div>
            <p className="text-sm text-[var(--ji-muted)] leading-relaxed mb-4">
              Already have a code? Enter it here to see how many friends have booked and how much credit you've earned.
            </p>
            <form onSubmit={onLookup} className="flex gap-2">
              <Input
                data-testid="refer-lookup-code-input"
                value={lookupCode}
                onChange={(e) => setLookupCode(e.target.value.toUpperCase())}
                placeholder="YOUR-CODE"
                className="h-12 flex-1 font-mono tracking-wider uppercase"
                maxLength={40}
              />
              <button type="submit" data-testid="refer-lookup-submit" disabled={lookupLoading} className="ji-btn-primary inline-flex items-center gap-2 px-4 h-12 text-sm font-semibold disabled:opacity-60" style={{ borderRadius: 4 }}>
                {lookupLoading ? <Spinner size={14} className="animate-spin" /> : "Check"}
              </button>
            </form>
            {summary && (
              <div className="mt-5 space-y-3 pt-4 border-t border-[var(--ji-border)]" data-testid="refer-lookup-result">
                <div className="text-xs uppercase tracking-[0.2em] text-[var(--ji-muted)]">{summary.owner_name}'s stats</div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="ji-card p-3" style={{ borderRadius: 4 }}>
                    <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold text-[var(--ji-primary)]">{summary.total_referrals}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--ji-muted)] mt-1">Referrals</div>
                  </div>
                  <div className="ji-card p-3" style={{ borderRadius: 4 }}>
                    <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold text-[var(--ji-accent)]">${summary.credit_balance.toFixed(0)}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--ji-muted)] mt-1">Credit</div>
                  </div>
                  <div className="ji-card p-3" style={{ borderRadius: 4 }}>
                    <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl font-bold text-[var(--ji-primary)]">{summary.pending}</div>
                    <div className="text-[10px] uppercase tracking-[0.15em] text-[var(--ji-muted)] mt-1">Pending</div>
                  </div>
                </div>
                <p className="text-xs text-[var(--ji-muted)] leading-relaxed">
                  <strong className="text-[var(--ji-text)]">Pending</strong> = friends booked but job not complete yet.
                  Credit is added once we finish their job.
                </p>
              </div>
            )}
          </div>

          <div className="ji-card p-6" style={{ borderRadius: 4 }}>
            <div className="ji-overline mb-3 inline-flex items-center gap-2"><ShieldCheck size={12} weight="fill" /> Fine print</div>
            <ul className="text-sm text-[var(--ji-muted)] space-y-2 leading-relaxed">
              <li>• Credit is applied after we complete the referred customer's paid job.</li>
              <li>• $25 discount off any Joseph Industries service — appliance, courier, cleanup, moving, assembly, or heavy lifting.</li>
              <li>• Not combinable with the 10% shop-buyer discount on the same job (use whichever saves you more).</li>
              <li>• No cash value. Not transferable. One referral code per email address.</li>
            </ul>
          </div>

          <Link to="/contact" data-testid="refer-book-now" className="ji-btn-accent inline-flex items-center justify-center gap-2 w-full p-6 group" style={{ borderRadius: 4 }}>
            <div>
              <div className="text-xs uppercase tracking-[0.22em] opacity-90">Have a code from a friend?</div>
              <div style={{ fontFamily: "Cabinet Grotesk" }} className="text-xl font-bold tracking-tight mt-1">Use it on the contact form →</div>
            </div>
          </Link>
        </aside>
      </section>
    </div>
  );
}
